import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  PRODUCTS_INDEX,
  searchClient,
  type TypesenseProduct
} from "@/lib/typesense";
import { TruncateTooltip } from "@repo/design-system";
import { ChevronDown, ChevronRight, ImageOff, Search } from "lucide-react";
import { memo, useState } from "react";
import { InstantSearch, useHits, useSearchBox } from "react-instantsearch";

import { TrackLineButton } from "./TrackLineButton";

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Resolve the variant to track from a search hit: the best live offer's
 * variant, else the first variant. The upsert endpoint has the final say on
 * purchasability, so a stale hit just gets rejected with a toast. */
function hitVariantId(hit: TypesenseProduct): string | null {
  return hit.meta?.best_offer?.variant_id ?? hit.variants?.[0]?.id ?? null;
}

/**
 * The in-editor **search-and-add** (surface 3 of #71) - lets a salon track a
 * product they haven't ordered yet, so the "My Stock" editor is not limited to
 * order history. Runs its own nested InstantSearch instance (independent of the
 * global header search's shared state) against the same products index, and
 * reuses {@link TrackLineButton} per result so tracked state + purchasability
 * errors are handled identically to the order-history "Track".
 */
export const AddProductDialog = memo(function AddProductDialog({
  open,
  onOpenChange
}: AddProductDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add a product to track</DialogTitle>
          <DialogDescription>
            Search the catalogue and track anything you keep on your shelf -
            even things you haven&apos;t ordered here yet.
          </DialogDescription>
        </DialogHeader>

        {/* Mount the search only while open so it isn't querying in the
            background, and each open starts clean. */}
        {open ? (
          <InstantSearch
            searchClient={searchClient}
            indexName={PRODUCTS_INDEX}
            future={{ preserveSharedStateOnUnmount: true }}
          >
            <StockProductSearch />
          </InstantSearch>
        ) : null}
      </DialogContent>
    </Dialog>
  );
});

/** Search box + results, using the enclosing (dialog-scoped) InstantSearch. */
function StockProductSearch() {
  const { query, refine } = useSearchBox();
  const { items } = useHits<TypesenseProduct>();

  const trimmed = query.trim();

  return (
    <div className="min-w-0">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        />
        <Input
          value={query}
          onChange={(event) => refine(event.target.value)}
          placeholder="Search products by name or brand…"
          aria-label="Search products to track"
          className="pl-9"
          autoFocus
        />
      </div>

      <ul className="mt-3 max-h-[45vh] space-y-1 overflow-y-auto">
        {trimmed === "" ? (
          <li className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Start typing to find products to track.
          </li>
        ) : items.length === 0 ? (
          <li className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No products match “{trimmed}”.
          </li>
        ) : (
          items.map((hit) => <ProductSearchResult key={hit.id} hit={hit} />)
        )}
      </ul>
    </div>
  );
}

/**
 * One product result. A single-variant product tracks directly; a product with
 * **more than one variant forces the salon to expand and pick the exact
 * variant** - a shelf count is per-variant, so we never guess which shade/size
 * they meant. Each variant reuses {@link TrackLineButton}.
 */
function ProductSearchResult({ hit }: { hit: TypesenseProduct }) {
  const [expanded, setExpanded] = useState(false);
  const title = hit.title ?? "Product";
  const thumbnail = hit.thumbnail ?? null;
  const variants = hit.variants ?? [];
  const multiVariant = variants.length > 1;

  return (
    <li className="hover:border-border rounded-md border border-transparent transition-colors">
      <div className="flex min-w-0 items-center gap-3 p-2">
        <div className="border-border flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-gray-50 dark:bg-neutral-900">
          <ProductThumbnailImage
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover"
            fallback={
              <ImageOff
                className="h-4 w-4 text-gray-300 dark:text-gray-600"
                aria-hidden="true"
              />
            }
          />
        </div>
        <div className="min-w-0 flex-1">
          <TruncateTooltip
            as="p"
            className="text-sm font-medium text-gray-900 dark:text-white"
          >
            {title}
          </TruncateTooltip>
          {hit.brand?.name ? (
            <TruncateTooltip
              as="p"
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              {hit.brand.name}
            </TruncateTooltip>
          ) : null}
        </div>
        <div className="flex-shrink-0">
          {multiVariant ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-label={`Choose a variant of ${title} to track`}
            >
              {expanded ? (
                <ChevronDown className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              )}
              Choose variant
              <span className="ml-1 text-gray-400">({variants.length})</span>
            </Button>
          ) : (
            <TrackLineButton
              variantId={variants[0]?.id ?? hitVariantId(hit)}
              title={title}
            />
          )}
        </div>
      </div>

      {multiVariant && expanded ? (
        <ul className="border-border space-y-1 border-t p-2 pl-3">
          {variants.map((variant) => {
            const variantTitle = variant.title || "Variant";
            const variantImage = variant.image ?? thumbnail;
            return (
              <li key={variant.id} className="flex min-w-0 items-center gap-3">
                <div className="border-border flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-gray-50 dark:bg-neutral-900">
                  <ProductThumbnailImage
                    src={variantImage ?? null}
                    alt=""
                    className="h-full w-full object-cover"
                    fallback={
                      <ImageOff
                        className="h-4 w-4 text-gray-300 dark:text-gray-600"
                        aria-hidden="true"
                      />
                    }
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <TruncateTooltip
                    as="p"
                    className="text-sm text-gray-900 dark:text-white"
                  >
                    {variantTitle}
                  </TruncateTooltip>
                  {variant.sku ? (
                    <TruncateTooltip
                      as="p"
                      className="text-xs text-gray-500 dark:text-gray-400"
                    >
                      SKU {variant.sku}
                    </TruncateTooltip>
                  ) : null}
                </div>
                <div className="flex-shrink-0">
                  <TrackLineButton
                    variantId={variant.id}
                    title={`${title} - ${variantTitle}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}
